import { useId } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { buildDatePickerButtonRenderer } from '@interxfi/design-system/components/input/date-picker-input/activators/button/date-picker-button-activator';
import { DatePickerInput } from '@interxfi/design-system/components/input/date-picker-input/date-picker-input';
import { useDateAfterTodayDeterminer } from '@interxfi/design-system/components/input/date-picker-input/util';
import { PageTabRail } from '../../../../../../../../../components/page-tab-rail/page-tab-rail';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../../../../../components/router/router-tabbed-navigation';
import {
  RaglStockRecordFilterableFields,
  RaglStockRecordTable,
  type RaglStockRecordTableHeaderProps,
} from '../../../../../../../../../features/clearing-ledger/stock-record/ragl-stock-record-table';
import { getPageHead } from '../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/operations/_operations/stock-record/_stock-record/')({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.ClearingOperationsCore,
      label: 'Stock Record',
      order: 0,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.ClearingOperationsCore>,
  head: () => getPageHead('Stock Record'),
  component: StockRecord,
});

const DatePickerActivatorRenderer = buildDatePickerButtonRenderer('en-US', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

function TableHeader({ filterProps }: RaglStockRecordTableHeaderProps) {
  const id = useId();
  const asOfValue = filterProps?.values?.asOf && 'date' in filterProps.values.asOf ? filterProps.values.asOf.date.exact : undefined;
  const dateAvailabilityDeterminer = useDateAfterTodayDeterminer();

  return (
    <PageTabRail
      actions={
        <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
          <DatePickerInput
            Activator={DatePickerActivatorRenderer}
            dateAvailabilityDeterminer={dateAvailabilityDeterminer}
            label="Select Date"
            name={`${id}-as-of-date`}
            onChange={(e) => {
              filterProps?.onFilterChange?.({
                ...filterProps?.values,
                [RaglStockRecordFilterableFields.AsOf]: { date: { exact: e.target.value } },
              });
            }}
            value={asOfValue}
          />
        </HorizontalStack>
      }
      group={TabbedNavigationGroup.ClearingOperationsCore}
    />
  );
}

function StockRecord() {
  return (
    <PageLayout>
      <PageLayout.Section noBorder>
        <RaglStockRecordTable HeaderActions={TableHeader} />
      </PageLayout.Section>
    </PageLayout>
  );
}
